import React, { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const LeadershipSection = () => {
  const [activeLeader, setActiveLeader] = useState(null);

  const leaders = [
    {
      name: "S. P. Ghosh (Aryan)",
      position: "Marketing Head & Founder & Director",
      image: "assets/owner/Aryan.png",
      imageAlt: "Professional Indian businessman",
      expertise: ["Corporate Event", "Brand Development", "Physical Marketing", "Digital Strategy", "Business Development", "Client Relations"],
      bio: "Orator Hype is led by a founder with a strong entrepreneurial background and a proven track record of building successful ventures. His journey began in 2012 with the establishment of FPS Kolkata, followed by the co-founding of FPS Pixel Pvt. Ltd. and Alexa Minds in West Bengal between 2017 and 2024—both of which continue to operate successfully today.\n\nWith a clear vision to scale and innovate, he went on to establish Orator Hype Pvt. Ltd. on 12th August 2025 in Mumbai, leveraging the city's strategic business ecosystem and growth potential. The company was founded with the objective of delivering integrated IT, branding, and digital solutions to businesses across India.\n\nBacked by extensive industry experience, strategic thinking, and a customer-first mindset, the founder continues to drive Orator Hype's growth with a focus on innovation, scalability, and long-term value creation across Pan-India markets.",
      linkedin: "https://www.linkedin.com/in/s-p-ghosh-aryan-351367374",
      email: "aryan@oratorhype.com",
      experience: "15+ years"
    },
    {
      name: "Koustov Choudhury",
      position: "Chief Operating Officer & Director",
      image: "assets/owner/koustav.png",
      imageAlt: "Professional Indian business leader",
      expertise: ["Cloud Architecture", "Cybersecurity", "DevOps", "Leader | Ex-IBM | Ex-Varian | Ex-Wipro"],
      bio: "Koustov is a seasoned IT and Cybersecurity professional with over 13 years of experience working with leading global organizations. He brings deep expertise in designing, managing, and scaling secure IT infrastructures for both SMBs and enterprise environments.\n\nAs the Chief Operating Officer, he plays a critical role in driving operational excellence, aligning technology with business objectives, and strengthening cybersecurity frameworks across the organization. His strong command over cloud solutions, IT governance, and infrastructure optimization enables seamless execution, effective risk management, and enhanced operational efficiency.\n\nWith a strategic mindset and hands-on leadership approach, Koustov currently leads technology strategy and operations, delivering customized IT, cloud, and cybersecurity solutions that support business growth, scalability, and long-term sustainability. His experience across global enterprises and customer-centric approach make him a key pillar in driving Orator Hype's technology vision and operational success.",
      linkedin: "https://www.linkedin.com/in/koustov-c",
      email: "kk@oratorhype.com",
      experience: "13+ years"
    },
    {
      name: "Sangita Choudhury",
      position: "Administration Head & Director",
      image: "assets/owner/Sangita.png",
      imageAlt: "Administration Head",
      expertise: ["Project Management", "Support Operations", "HR", "Admin"],
      bio: "Sangita is a highly experienced professional with over 10+ years of expertise in corporate administration, HR operations, and office management. She brings strong organizational skills, process-driven execution, and a people-first approach that ensures smooth day-to-day operations across the organization.\n\nAs the Head of Administration, she plays a vital role in managing administrative functions, HR coordination, internal operations, compliance, and office management. Her ability to streamline processes, maintain operational discipline, and support cross-functional teams contributes significantly to organizational efficiency and workplace excellence.\n\nWith a deep understanding of corporate environments and employee management, Sangita ensures seamless coordination between teams, effective resource management, and a positive work culture. Her professionalism, reliability, and attention to detail make her a key pillar in driving operational stability and supporting the company's long-term growth.",
      email: "admin@oratorhype.com",
      experience: "10+ years"
    },
    {
      name: "Pranjali Balekar",
      position: "PR & Media Relations Manager",
      image: "assets/owner/Pranjali.png",
      imageAlt: "PR & Media Relations Manager",
      expertise: ["Brand Strategy", "PR", "Influencer Marketing"],
      bio: "With over 17+ years of experience in the Indian film and television industry, Pranjali brings a rare combination of creative expertise, industry insight, and media relationship management to the world of Public Relations.\n\nShe began her professional journey as a Hair Stylist, working closely with leading artists, production houses, and directors across Bollywood, regional cinema, television, and digital platforms. Over the years, her hands-on experience on sets and deep understanding of the entertainment ecosystem naturally evolved into a strong career in PR & Media Relations.\n\nFilm & Entertainment Experience:\n• Hollywood: Queen of the Mountain\n• Bollywood: London Dreams (2009), Golmaal (Series 1–4), Indu Sarkar, Judgemental Hai Kya, Coolie No. 2, Ishq Forever, Nanu Ki Jaanu, Adipurush, Hichki, Dabbang-3\n• South Indian Cinema: Ponniyin Selvan Part 1 & 2, Laxmi's NTR, Toxic – Part 1 & 2\n• OTT & Digital: CAT – Netflix Series, Pritam Pedro (Upcoming), Indian Police Force (Prime Video)\n• Television & Reality Shows: Taarak Mehta Ka Ooltah Chashmah, Dance India Dance, Comedy Circus Ke Tansen, Jhalak Dikhhla Jaa (Seasons 1–9), Ladies Special – Star Plus, TED Talk (India)",
      email: "sales@oratorhype.com",
      experience: "17+ years"
    }
  ];

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (activeLeader) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [activeLeader]);

  // Format bio with line breaks
  const formatBio = (bio) => {
    return bio.split('\n').map((line, index) => {
      if (line.trim() === '') {
        return <div key={index} className="h-4" />;
      }
      if (line.includes(':')) {
        const [title, ...content] = line.split(':');
        return (
          <div key={index} className="mb-2">
            <strong className="text-white font-semibold">{title}:</strong>
            <span className="text-white/80">{content.join(':')}</span>
          </div>
        );
      }
      if (line.startsWith('•')) {
        return (
          <div key={index} className="flex items-start ml-4 mb-1">
            <span className="text-cyan-300 mr-2">•</span>
            <span className="text-white/80">{line.substring(2)}</span>
          </div>
        );
      }
      return <p key={index} className="mb-3 text-white/80 leading-relaxed">{line}</p>;
    });
  };

  return (
    <>
      <section className="relative py-20 px-4 overflow-hidden">
        {/* Background gradient with blur */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#2642B2] via-indigo-900 to-slate-900 backdrop-blur-3xl" />
        
        {/* Animated grid background */}
        <div className="absolute inset-0 bg-[linear-gradient(transparent_1px,#0001_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,black_70%,transparent_110%)] opacity-20" />

        <div className="relative max-w-7xl mx-auto z-10">
          {/* Header with glass effect */}
          <div className="text-center mb-16">
            <div className="inline-block backdrop-blur-sm bg-white/5 border border-white/10 rounded-2xl px-8 py-4 mb-6">
              <span className="text-cyan-300 font-semibold text-sm tracking-wider uppercase">
                Leadership Team
              </span>
            </div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-white via-cyan-100 to-blue-100 bg-clip-text text-transparent">
              Meet the Minds Behind
              <span className="block mt-2">Our Success</span>
            </h2>
            
            <p className="max-w-2xl mx-auto text-lg text-white/80">
              Our leadership team combines <span className="text-cyan-300 font-medium">strategy</span>, 
              <span className="text-blue-300 font-medium"> technology</span>, and 
              <span className="text-pink-300 font-medium"> creativity</span> to drive impactful results.
            </p>
          </div>

          {/* Leaders Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {leaders.map((leader, index) => (
              <div
                key={index}
                className="group relative"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Glass card with gradient border */}
                <div className="relative backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 border border-white/20 rounded-3xl p-6 shadow-2xl hover:shadow-cyan-500/20 transition-all duration-500 hover:-translate-y-2 overflow-hidden">
                  
                  {/* Hover glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/0 via-blue-500/0 to-pink-500/0 group-hover:from-cyan-500/10 group-hover:via-blue-500/5 group-hover:to-pink-500/10 transition-all duration-500" />
                  
                  {/* Experience badge */}
                  <div className="absolute top-4 right-4 backdrop-blur-sm bg-cyan-500/20 border border-cyan-500/30 rounded-full px-3 py-1">
                    <span className="text-cyan-300 text-xs font-semibold">{leader.experience}</span>
                  </div>
                  
                  {/* Content */}
                  <div className="relative flex flex-col sm:flex-row gap-6 items-center sm:items-start">
                    {/* Image with glass frame */}
                    <div className="relative">
                      <div className="absolute -inset-2 bg-gradient-to-r from-cyan-500/30 to-blue-500/30 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="relative w-32 h-32 rounded-2xl overflow-hidden border-2 border-white/30 bg-gradient-to-br from-white/20 to-transparent p-1">
                        <Image
                          src={leader.image}
                          alt={leader.imageAlt}
                          className="w-full h-full object-cover rounded-xl"
                        />
                        {/* Image shine effect */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                      </div>
                    </div>

                    {/* Text content */}
                    <div className="flex-1 text-center sm:text-left">
                      <h3 className="text-xl lg:text-2xl font-bold text-white mb-1">
                        {leader.name}
                      </h3>
                      <p className="text-cyan-300 font-medium text-sm lg:text-base mb-3">
                        {leader.position}
                      </p>

                      {/* Short bio with fade effect */}
                      <div className="relative">
                        <p className="text-white/70 text-sm line-clamp-3 mb-4 group-hover:text-white/90 transition-colors duration-300">
                          {leader.bio.split('\n')[0]}
                        </p>
                        <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-transparent to-[#0f172a]/0" />
                      </div>

                      {/* Action buttons */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-4">
                        <button
                          onClick={() => setActiveLeader(leader)}
                          className="px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-lg text-cyan-300 font-medium hover:from-cyan-500/30 hover:to-blue-500/30 hover:text-white hover:border-cyan-400 transition-all duration-300 text-sm flex items-center justify-center gap-2 group/btn"
                        >
                          <span>View Profile</span>
                          <Icon 
                            name="ArrowRight" 
                            size={16} 
                            className="transform group-hover/btn:translate-x-1 transition-transform" 
                          />
                        </button>

                        {/* Social icons */}
                        <div className="flex gap-3 justify-center sm:justify-start">
                          {leader.linkedin && (
                            <a
                              href={leader.linkedin}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-10 h-10 rounded-lg backdrop-blur-sm bg-white/10 border border-white/20 flex items-center justify-center hover:bg-cyan-500/30 hover:border-cyan-400 hover:scale-110 transition-all duration-300 group/icon"
                              aria-label={`Connect with ${leader.name} on LinkedIn`}
                            >
                              <Icon 
                                name="Linkedin" 
                                size={18} 
                                className="text-white/80 group-hover/icon:text-white" 
                              />
                            </a>
                          )}

                          <a
                            href={`mailto:${leader.email}`}
                            className="w-10 h-10 rounded-lg backdrop-blur-sm bg-white/10 border border-white/20 flex items-center justify-center hover:bg-blue-500/30 hover:border-blue-400 hover:scale-110 transition-all duration-300 group/icon"
                            aria-label={`Email ${leader.name}`}
                          >
                            <Icon 
                              name="Mail" 
                              size={18} 
                              className="text-white/80 group-hover/icon:text-white" 
                            />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Corner accent */}
                  <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-cyan-500/20 to-blue-500/20 rounded-bl-3xl rounded-tr-3xl -translate-y-1/2 translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= MODAL ================= */}
      {activeLeader && (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center px-4"
    onClick={() => setActiveLeader(null)}
  >
    {/* Backdrop */}
    <div className="absolute inset-0 bg-black/60 backdrop-blur-md" />

    {/* Modal Wrapper */}
    <div
      className="relative w-full max-w-4xl h-[90vh]"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Modal Box */}
      <div className="relative h-full backdrop-blur-2xl bg-gradient-to-br from-white/15 to-white/5 border border-white/20 rounded-3xl shadow-2xl flex flex-col overflow-hidden">

        {/* Close Button */}
        <button
          onClick={() => setActiveLeader(null)}
          className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center hover:bg-white/20"
        >
          <Icon name="X" size={20} className="text-white" />
        </button>

        {/* Header */}
        <div className="p-6 border-b border-white/10 flex-shrink-0">
          <h3 className="text-2xl font-bold text-white">
            {activeLeader.name}
          </h3>
          <p className="text-cyan-300">{activeLeader.position}</p>
        </div>

        {/* ✅ SCROLLABLE CONTENT (FIXED) */}
        <div
          className="
            flex-1
            overflow-y-auto
            overscroll-contain
            touch-pan-y
            custom-scrollbar
            p-6
          "
        >
          {/* About */}
          <div className="mb-8">
            <h4 className="text-xl font-bold text-white mb-3">About</h4>
            {formatBio(activeLeader.bio)}
          </div>

          {/* Expertise */}
          <div className="mb-8">
            <h4 className="text-xl font-bold text-white mb-3">
              Areas of Expertise
            </h4>
            <div className="flex flex-wrap gap-3">
              {activeLeader.expertise.map((skill, idx) => (
                <span
                  key={idx}
                  className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className="mb-6">
            <h4 className="text-xl font-bold text-white mb-3">Connect</h4>
            <div className="flex flex-col sm:flex-row gap-4">
              {activeLeader.linkedin && (
                <a
                  href={activeLeader.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-4 bg-cyan-500/20 border border-cyan-500/30 rounded-xl text-white"
                >
                  LinkedIn
                </a>
              )}
              <a
                href={`mailto:${activeLeader.email}`}
                className="px-6 py-4 bg-blue-500/20 border border-blue-500/30 rounded-xl text-white"
              >
                {activeLeader.email}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
)}


      {/* Add custom styles */}
      <style jsx>{`
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }

        /* Custom scrollbar */
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(59, 130, 246, 0.5) transparent;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #22d3ee, #a855f7);
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #06b6d4, #9333ea);
        }
      `}</style>
    </>
  );
};

export default LeadershipSection;